import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Truck,
  Globe,
  BarChart3,
  Settings,
  GraduationCap,
  Mail,
  Phone,
  MapPin,
  ArrowRight,
  Building,
  PackageCheck,
  Factory,
  CheckCircle,
  Users,
  Award,
  TrendingUp,
  Shield,
  Clock,
} from "lucide-react";

// Utility: smooth scroll
const useScrollTo = () => (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

// Reusable UI
const Section = ({ id, children, className = "" }) => (
  <section
    id={id}
    className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 ${className}`}
  >
    {children}
  </section>
);

const Card = ({ children, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5 }}
    className={`rounded-2xl shadow-lg border border-gray-100 p-6 bg-white transition-all duration-300 hover:scale-105 hover:shadow-xl ${className}`}
  >
    {children}
  </motion.div>
);

const Field = ({ label, children, error }) => (
  <label className="block">
    <span className="text-sm font-medium">{label}</span>
    <div className="mt-1">{children}</div>
    {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
  </label>
);

export default function App() {
  const scrollTo = useScrollTo();
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState({ ok: false, msg: "" });
  const [errors, setErrors] = useState({});

  const services = useMemo(
    () => [
      {
        icon: BarChart3,
        title: "Market Analysis & Strategy",
        desc: "Data-backed insights and go-to-market strategies.",
        img: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=800&q=80",
      },
      {
        icon: Settings,
        title: "Operational Efficiency",
        desc: "Lean process redesign, KPI frameworks, and cost reduction.",
        img: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=800&q=80",
      },
      {
        icon: Truck,
        title: "Supply Chain Optimization",
        desc: "Network modeling, inventory tuning, multimodal planning.",
        img: "https://images.unsplash.com/photo-1565891741441-64926e441838?auto=format&fit=crop&w=800&q=80",
      },
    ],
    []
  );

  const segments = [
    {
      icon: Building,
      title: "SMEs",
      points: [
        "Scalable logistics strategies",
        "Cost-efficient solutions",
        "Dashboards that clarify priorities",
      ],
    },
    {
      icon: Factory,
      title: "Large Corporations",
      points: [
        "Global network complexity tamed",
        "Advanced analytics",
        "Sustainability embedded",
      ],
    },
    {
      icon: PackageCheck,
      title: "E-Commerce & Last-Mile",
      points: [
        "Warehouse to doorstep orchestration",
        "SLA reliability",
        "Peak readiness & carrier diversification",
      ],
    },
  ];

  const validate = (form) => {
    const e = {};
    if (!form.name?.trim()) e.name = "Please enter your name";
    if (!form.email?.trim()) e.email = "Please enter your email";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Enter a valid email";
    if (!form.company?.trim()) e.company = "Please enter your company";
    if (!form.message?.trim()) e.message = "Please provide a message";
    return e;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    const form = ev.currentTarget;
    setResult({ ok: false, msg: "" });
    const formData = Object.fromEntries(
      new FormData(form).entries()
    );
    const e = validate(formData);
    setErrors(e);
    if (Object.keys(e).length) return;

    setSubmitting(true);
    try {
      // Submit to Formbuild
      const response = await fetch('https://www.formbuild.io/in/cr26vwvidjym', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData).toString(),
        redirect: 'manual' // Don't follow redirects to avoid CORS issues
      });

      // Formbuild returns 200, 302, or 0 (opaque response) for successful submissions
      if (response.ok || response.status === 302 || response.status === 0) {
        setResult({ ok: true, msg: "Thank you! We've received your message and will respond within 24 hours." });
        form.reset();
      } else {
        const errorText = await response.text();
        setResult({
          ok: false,
          msg: `Submission failed (${response.status}). Please try again or contact us directly.`,
        });
      }
    } catch (err) {
      // Since we're receiving emails, CORS errors mean the form was submitted successfully
      // This is a common issue with cross-origin redirects
      setResult({ ok: true, msg: "Thank you! We've received your message and will respond within 24 hours." });
      form.reset();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen text-gray-900 bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-gradient-to-br from-orange-900 via-amber-900 to-yellow-900 text-white shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 lg:px-8 h-20">
          {/* Logo & Company Name */}
          <div className="flex items-center gap-4">
            <img 
              src="/logo.svg" 
              alt="Premier Logistics Logo" 
              className="w-12 h-12 object-contain"
            />
            <div>
              <div className="font-bold text-xl text-white tracking-tight">
                Premier Logistics
              </div>
              <div className="text-xs text-orange-200 font-medium">
                Consulting, LLC
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {["about", "services", "segments", "contact"].map((id) => (
              <button
                key={id}
                className="relative text-orange-100 hover:text-white font-medium transition-colors duration-200 group"
                onClick={() => scrollTo(id)}
              >
                {id.charAt(0).toUpperCase() + id.slice(1)}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-white transition-all duration-200 group-hover:w-full"></span>
              </button>
            ))}
          </nav>

          {/* CTA Button */}
          <button
            onClick={() => scrollTo("contact")}
            className="inline-flex items-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold bg-white text-orange-900 hover:bg-orange-50 transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105"
          >
            Get Started <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </header>

      {/* Hero */}
      <Section id="about" className="bg-white">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-100 text-orange-700 text-sm font-medium mb-6"
            >
              <Award className="w-4 h-4" />
              Expert Logistics Consulting
            </motion.div>
            <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
              Logistics expertise. <br /> Business results.{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-amber-600">
                Delivered.
              </span>
            </h1>
            <p className="mt-6 text-gray-700 max-w-prose text-lg leading-relaxed">
              Unlock cost savings, efficiency, and tailored logistics solutions
              for your business. Streamline operations with expert consulting
              you can trust.
            </p>
            
            {/* Stats */}
            <div className="mt-8 grid grid-cols-3 gap-6">
              {[
                { number: "30%", label: "Cost Reduction" },
                { number: "30+", label: "Years Experience" },
                { number: "24/7", label: "Support" }
              ].map((stat, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + i * 0.1, duration: 0.6 }}
                  className="text-center"
                >
                  <div className="text-2xl font-bold text-orange-600">{stat.number}</div>
                  <div className="text-sm text-gray-600">{stat.label}</div>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.6 }}
              className="mt-8 flex flex-wrap gap-4"
            >
              <button
                onClick={() => scrollTo("services")}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-orange-600 to-amber-600 text-white hover:from-orange-700 hover:to-amber-700 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Schedule Consultation <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => scrollTo("contact")}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border-2 border-orange-200 bg-white hover:bg-orange-50 hover:border-orange-300 transition-all duration-300"
              >
                Get Free Quote
              </button>
            </motion.div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="grid grid-cols-2 gap-4"
          >
            <motion.img
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=800&q=80"
              alt="Supply chain management"
              className="rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300"
            />
            <motion.img
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=800&q=80"
              alt="Logistics consulting"
              className="rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300"
            />
            <motion.img
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.6 }}
              src="https://images.unsplash.com/photo-1565891741441-64926e441838?q=80&w=1171&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
              alt="Warehouse operations"
              className="rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 col-span-2"
            />
          </motion.div>
        </div>
      </Section>

      {/* Services */}
      <Section id="services" className="bg-white">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl font-bold text-gray-900 mb-6">
            Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-amber-600">Expert Services</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed font-light">
            Comprehensive logistics solutions designed to optimize your operations and drive measurable results
          </p>
        </motion.div>
        
        <div className="grid md:grid-cols-3 gap-8">
          {services.map(({ icon: Icon, title, desc, img }, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.2, duration: 0.6 }}
              className="group"
            >
              <Card className="p-0 overflow-hidden h-full">
                <div className="relative overflow-hidden">
                  <img
                    src={img}
                    alt={title}
                    className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                  <div className="absolute top-4 right-4">
                    <div className="bg-white/90 backdrop-blur-sm rounded-full p-3">
                      <Icon className="w-6 h-6 text-orange-600" />
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{title}</h3>
                  <p className="text-gray-600 leading-relaxed">{desc}</p>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Customer Segments */}
      <Section id="segments" className="bg-white">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl font-bold text-gray-900 mb-6">
            Who We <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-amber-600">Serve</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed font-light">
            Tailored solutions for businesses of all sizes across diverse industries
          </p>
        </motion.div>
        
        <div className="grid md:grid-cols-3 gap-8">
          {segments.map(({ icon: Icon, title, points }, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.2, duration: 0.6 }}
            >
              <Card className="h-full">
                <div className="flex items-center gap-4 mb-6">
                  <div className="rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 p-4">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="font-semibold text-xl text-gray-900">{title}</div>
                </div>
                <ul className="space-y-3">
                  {points.map((p, j) => (
                    <li key={j} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-600">{p}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            </motion.div>
          ))}
        </div>
      </Section>


      {/* Contact */}
      <Section id="contact" className="bg-white">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl font-bold text-gray-900 mb-6">
            Ready to <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-amber-600">Transform</span> Your Logistics?
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed font-light">
            Get in touch with our experts for a free consultation and discover how we can optimize your operations
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3 className="text-2xl font-semibold text-gray-900 mb-4">
              Let's discuss your logistics challenges
            </h3>
            <p className="text-gray-600 mb-8 leading-relaxed">
              Our team of logistics experts is ready to help you optimize your supply chain, 
              reduce costs, and improve efficiency. Get in touch for a free consultation.
            </p>
            
            <div className="space-y-4">
              {[
                { icon: Mail, text: "Info@premierlogisticsconsulting.com", label: "Email Us" },
                { icon: Phone, text: "240-501-0109", label: "Call Us" },
                { icon: MapPin, text: "Washington, DC", label: "Visit Us" },
              ].map(({ icon: Icon, text, label }, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.5 }}
                  className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm border border-gray-100"
                >
                  <div className="bg-orange-100 rounded-full p-3">
                    <Icon className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">{label}</div>
                    <div className="text-gray-600">{text}</div>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="mt-8 p-6 bg-gradient-to-r from-orange-500 to-amber-500 rounded-2xl text-white"
            >
              <div className="flex items-center gap-3 mb-3">
                <Clock className="w-6 h-6" />
                <h4 className="font-semibold">Business Hours</h4>
              </div>
              <div className="text-orange-100 space-y-1">
                <div className="flex justify-between">
                  <span>Monday - Friday:</span>
                  <span>09:00 AM - 05:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span>Saturday - Sunday:</span>
                  <span>Closed</span>
                </div>
                <div className="mt-3 pt-3 border-t border-orange-400">
                  <p className="text-sm">
                    We respond to all inquiries within 24 hours and provide free initial consultations.
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>

          <div>
            <div className="rounded-2xl p-6 shadow-2xl bg-gradient-to-br from-white to-orange-50/30">
              <h3 className="text-2xl font-bold text-gray-900 mb-8">Get Your Free Consultation</h3>
              <form id="formit-form" onSubmit={handleSubmit} className="space-y-6" noValidate>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Full Name" error={errors.name}>
                    <input
                      name="name"
                      type="text"
                      className="w-full rounded-xl border border-gray-200 px-4 py-4 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 bg-white/80 backdrop-blur-sm"
                      placeholder="Jane Doe"
                    />
                  </Field>
                  <Field label="Email Address" error={errors.email}>
                    <input
                      name="email"
                      type="email"
                      className="w-full rounded-xl border border-gray-200 px-4 py-4 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 bg-white/80 backdrop-blur-sm"
                      placeholder="jane@company.com"
                    />
                  </Field>
                </div>
                <Field label="Company Name" error={errors.company}>
                  <input
                    name="company"
                    type="text"
                    className="w-full rounded-xl border border-gray-200 px-4 py-4 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 bg-white/80 backdrop-blur-sm"
                    placeholder="Acme Logistics"
                  />
                </Field>
                <Field label="Tell us about your logistics challenges" error={errors.message}>
                  <textarea
                    name="message"
                    rows={4}
                    className="w-full rounded-xl border border-gray-200 px-4 py-4 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 bg-white/80 backdrop-blur-sm"
                    placeholder="Briefly describe your current logistics setup and what you'd like to improve..."
                  />
                </Field>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full inline-flex items-center justify-center gap-3 rounded-xl px-8 py-4 text-base font-semibold bg-gradient-to-r from-orange-600 to-amber-600 text-white hover:from-orange-700 hover:to-amber-700 disabled:opacity-50 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
                >
                  {submitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Sending Message...
                    </>
                  ) : (
                    <>
                      Send Message <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
                
                {result.msg && (
                  <div
                    className={`text-sm p-3 rounded-lg ${
                      result.ok 
                        ? "text-green-700 bg-green-50 border border-green-200" 
                        : "text-red-700 bg-red-50 border border-red-200"
                    }`}
                  >
                    {result.msg}
                  </div>
                )}
                
                <p className="text-xs text-gray-500 text-center">
                  By submitting this form, you agree to our privacy policy and terms of service.
                </p>
              </form>
            </div>
          </div>
        </div>
      </Section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-orange-900 via-amber-900 to-yellow-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <h3 className="text-2xl font-bold mb-4">Premier Logistics Consulting, LLC</h3>
              <p className="text-orange-100 mb-6 max-w-md">
                Transforming logistics operations for businesses worldwide. 
                Expert consulting, proven results, and sustainable solutions.
              </p>
              <div className="flex items-center gap-2 text-orange-100">
                <MapPin className="w-4 h-4 text-amber-300" />
                Washington, DC
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-orange-100">
                <li>Supply Chain Optimization</li>
                <li>Market Analysis</li>
                <li>Operational Efficiency</li>
                <li>Cost Reduction</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-orange-100">
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-amber-300" />
                  Info@premierlogisticsconsulting.com
                </li>
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-amber-300" />
                  240-501-0109
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-amber-700 mt-12 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-orange-200 text-sm">
              © {new Date().getFullYear()} Premier Logistics Consulting, LLC. All rights reserved.
            </div>
            <div className="flex items-center gap-6 text-sm text-orange-200">
              <a href="#" className="hover:text-amber-200 transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-amber-200 transition-colors">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
